import { EVERYONE, SABOTEURS } from '@continuum/show-core';
import type { ChatTo, WireChat } from '@continuum/show-core';

/*
 * How one phone's messages fall into conversations, and which of them it has not read. Pure, so
 * the list, the badge and the tests all count the same way.
 */

/** A conversation: with one crewmate (by seat), the whole table, or the saboteurs' channel. */
export type ThreadKey = ChatTo;

/** Per conversation, the last message this phone has read (by `n`). */
export type ReadMarks = Record<string, number>;

/** Per conversation, then per seat: how far the others have read it, and when (from the room). */
export type SeenMarks = Record<string, Record<string, { n: number; at: number }>>;

export function threadOf(message: Pick<WireChat, 'from' | 'to'>, me: number): ThreadKey {
  if (message.to === SABOTEURS || message.to === EVERYONE) return message.to;
  return message.from === me ? message.to : message.from;
}

/** A conversation kept as a string (a read mark's key) back as the room knows it. */
export function threadFromKey(key: string): ThreadKey | null {
  if (key === SABOTEURS || key === EVERYONE) return key;
  const seat = Number(key);
  return Number.isInteger(seat) && seat > 0 ? seat : null;
}

/** Whether a conversation has more than two people in it: the table, or the saboteurs. */
export function isGroup(key: ThreadKey): key is typeof SABOTEURS | typeof EVERYONE {
  return key === SABOTEURS || key === EVERYONE;
}

export function inThread(messages: readonly WireChat[], me: number, key: ThreadKey): WireChat[] {
  return messages.filter((m) => threadOf(m, me) === key);
}

/** Messages from someone else, newer than what this phone has read there. */
export function unreadIn(messages: readonly WireChat[], me: number, read: ReadMarks, key: ThreadKey): number {
  const upTo = read[String(key)] ?? 0;
  return inThread(messages, me, key).filter((m) => m.from !== me && m.n > upTo).length;
}

/** The badge: everything unread, the saboteurs' channel only while this phone is in it. */
export function unreadTotal(messages: readonly WireChat[], me: number, read: ReadMarks, saboteurs: boolean): number {
  return messages.filter((m) => {
    if (m.from === me) return false;
    const key = threadOf(m, me);
    if (key === SABOTEURS && !saboteurs) return false;
    return m.n > (read[String(key)] ?? 0);
  }).length;
}

/** Having read a conversation to its end. */
export function markRead(read: ReadMarks, messages: readonly WireChat[], me: number, key: ThreadKey): ReadMarks {
  const last = inThread(messages, me, key).at(-1)?.n ?? 0;
  if ((read[String(key)] ?? 0) >= last) return read;
  return { ...read, [String(key)]: last };
}

export interface ThreadRow {
  key: ThreadKey;
  last: WireChat | null;
  unread: number;
}

/**
 * The list: the saboteurs' channel first when this phone is in it, then the whole table, then every
 * crewmate, the latest conversation first and the rest in seat order.
 */
export function threadRows(
  messages: readonly WireChat[],
  me: number,
  read: ReadMarks,
  crew: readonly number[],
  saboteurs: boolean,
): ThreadRow[] {
  const row = (key: ThreadKey): ThreadRow => ({
    key,
    last: inThread(messages, me, key).at(-1) ?? null,
    unread: unreadIn(messages, me, read, key),
  });
  const people = crew
    .filter((seat) => seat !== me)
    .map(row)
    .sort((a, b) => (b.last?.n ?? 0) - (a.last?.n ?? 0) || (a.key as number) - (b.key as number));
  return [...(saboteurs ? [row(SABOTEURS)] : []), row(EVERYONE), ...people];
}

/** What shows under this phone's own latest message: still only sent, or read — when, and by whom. */
export type Receipt =
  | { kind: 'sent' }
  | { kind: 'read'; at: number }
  | { kind: 'readBy'; seats: number[]; all: boolean };

/**
 * Sent and Read for this phone's latest message in a conversation (Marek, 2026-10-24). A private
 * one is read when the other guest has read up to it; in the table or the saboteurs' channel, it
 * says who has, among `members` (everyone else in it now).
 */
export function receiptFor(
  messages: readonly WireChat[],
  me: number,
  key: ThreadKey,
  seen: SeenMarks,
  members: readonly number[],
): { n: number; receipt: Receipt } | null {
  const mine = inThread(messages, me, key)
    .filter((m) => m.from === me)
    .at(-1);
  if (!mine) return null;
  const marks = seen[String(key)] ?? {};
  if (!isGroup(key)) {
    const mark = marks[String(key)];
    return { n: mine.n, receipt: mark && mark.n >= mine.n ? { kind: 'read', at: mark.at } : { kind: 'sent' } };
  }
  const others = members.filter((s) => s !== me);
  const seats = others.filter((s) => (marks[String(s)]?.n ?? 0) >= mine.n);
  if (seats.length === 0) return { n: mine.n, receipt: { kind: 'sent' } };
  return { n: mine.n, receipt: { kind: 'readBy', seats, all: seats.length === others.length } };
}
